/**
 * List view (A) of the gated scoring pipeline.
 *
 * Three vertical stages with readable text rows — best when you want to
 * actually read what each specialist said. The graph view (B) is better
 * for spotting causality; this view wins for comprehension density.
 */
import React from 'react';
import {Box, Chip, Collapse, IconButton, Paper, Tooltip, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltIcon from '@mui/icons-material/Bolt';
import {
  AgeBand,
  AgeBandVerdict,
  AgentScore,
  EvalAgentEntry,
  FeedbackLog,
  SpecialistSelectionReason,
} from '../../../types/generated/graphql';
import {pcolors, cardSx} from '../../../theme/planapusTheme';
import {humanizeSpecialization} from '../../Admin/agentLabels';
import {
  agentScoreTone,
  humanizeTag,
  humanizeFlag,
  verdictTone,
  useChainData,
  SpecialistEntry,
} from './useChainData';

// Canonical display order for the four age bands the finalize agent
// emits. Sorting here (rather than trusting server order) keeps the UI
// stable even if the backend re-orders the array.
const AGE_BAND_ORDER: AgeBand[] = [AgeBand.Toddler, AgeBand.Kid, AgeBand.Tween, AgeBand.Teen];

function ageBandLabel(band: AgeBand, minAge: number, maxAge: number): string {
  const name = band.charAt(0) + band.slice(1).toLowerCase();
  return `${name} ${minAge}–${maxAge}`;
}

interface Props {
  scores: AgentScore[];
  agents: EvalAgentEntry[];
  childAge?: number;
  feedback?: FeedbackLog[];
}

function selectionLabel(
  reason: SpecialistSelectionReason | string,
  detail: string | null | undefined,
): {text: string; accent: string} {
  switch (reason) {
    case SpecialistSelectionReason.Always:
      return {text: 'Always-on', accent: pcolors.textSecondary};
    case SpecialistSelectionReason.CategoryTag:
      return {text: detail ? `Category: ${humanizeTag(detail)}` : 'Category', accent: pcolors.blue[500]};
    case SpecialistSelectionReason.RiskFlag:
      return {text: detail ? `Flag: ${humanizeFlag(detail)}` : 'Risk flag', accent: pcolors.amber[500]};
    case SpecialistSelectionReason.Triage:
      return {text: 'Triage', accent: pcolors.textSecondary};
    case SpecialistSelectionReason.Final:
      return {text: 'Final verdict', accent: pcolors.textSecondary};
    default:
      return {text: 'Legacy', accent: pcolors.textMuted};
  }
}

export function ScoringChainList({scores, agents, childAge = 10, feedback = []}: Props) {
  const {
    triage,
    final,
    specialists,
    skipped,
    didGate,
    shortCircuited,
    triageTags,
    triageFlags,
    channelGateScore,
  } = useChainData(scores, agents, childAge, feedback);

  if (channelGateScore) {
    return <ChannelGateBanner score={channelGateScore} />;
  }

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
      <StageBlock
        label="Stage 0 · Triage"
        subtitle={
          triage
            ? 'Cheap summary-only pass that tags and flags the video to decide which specialists run.'
            : 'No triage row recorded — the legacy pipeline fanned out to every applicable agent.'
        }
      >
        {triage ? (
          <TriageRow score={triage} />
        ) : (
          <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
            This video was scored before the gated pipeline existed.
          </Typography>
        )}
      </StageBlock>

      {shortCircuited ? (
        <Paper
          elevation={0}
          sx={{...cardSx, p: 1.5, bgcolor: pcolors.amber[50], borderColor: pcolors.amber[100]}}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <BoltIcon sx={{fontSize: 18, color: pcolors.amber[700]}} />
            <Typography sx={{fontSize: 13, fontWeight: 700, color: pcolors.amber[700]}}>
              Short-circuited
            </Typography>
          </Box>
          <Typography sx={{fontSize: 12, color: pcolors.textPrimary, mt: 0.5}}>
            {triage?.triageOutput?.shortCircuitReason ||
              'Triage was confident enough that specialists and the final verdict were skipped.'}
          </Typography>
        </Paper>
      ) : (
        <>
          <StageBlock
            label="Stage 1 · Specialists"
            subtitle={
              didGate
                ? `Only the specialists triggered by the triage's tags/flags ran — ${specialists.length} of ${
                    specialists.length + skipped.length
                  } applicable.`
                : 'Every applicable specialist ran in parallel.'
            }
          >
            {specialists.length === 0 ? (
              <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
                No specialist scores recorded for this video yet.
              </Typography>
            ) : (
              <Box sx={{display: 'grid', gap: 0.5, gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}}}>
                {specialists.map(entry => (
                  <SpecialistRow key={entry.specialization} entry={entry} />
                ))}
              </Box>
            )}
            <SkippedSection
              skipped={skipped}
              triageTags={triageTags}
              triageFlags={triageFlags}
            />
          </StageBlock>

          <StageBlock
            label="Stage 2 · Final verdict"
            subtitle="Meta-agent synthesizes the specialists' outputs into an age-weighted verdict."
          >
            {final ? (
              <FinalRow score={final} childAge={childAge} />
            ) : didGate ? (
              <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
                Final verdict hasn't been computed yet (or failed — check the admin jobs panel).
              </Typography>
            ) : (
              <Typography sx={{fontSize: 12, color: pcolors.textMuted}}>
                Legacy scores — the final verdict is derived from the aggregate rather than a meta-agent.
              </Typography>
            )}
          </StageBlock>
        </>
      )}
    </Box>
  );
}

function StageBlock({
  label,
  subtitle,
  children,
}: {
  label: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: pcolors.textPrimary,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {label}
      </Typography>
      {subtitle && (
        <Typography sx={{fontSize: 11, color: pcolors.textSecondary, mt: 0.25, mb: 0.75}}>
          {subtitle}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function TriageRow({score}: {score: AgentScore}) {
  const t = score.triageOutput;
  const tone = verdictTone(t?.verdict);
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
      <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75}}>
        <Chip
          size="small"
          label={`Verdict: ${tone.label}`}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            bgcolor: tone.bg,
            color: tone.fg,
            textTransform: 'capitalize',
          }}
        />
        {t?.confidence != null && (
          <Chip
            size="small"
            label={`conf ${(t.confidence * 100).toFixed(0)}%`}
            sx={{
              fontSize: 11,
              bgcolor: pcolors.borderLight,
              color: pcolors.textSecondary,
              fontVariantNumeric: 'tabular-nums',
            }}
          />
        )}
      </Box>

      {(t?.categoryTags?.length ?? 0) > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5}}>
          <Typography
            sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4}}
          >
            categories
          </Typography>
          {t!.categoryTags.map(tag => (
            <Chip
              key={tag}
              size="small"
              label={humanizeTag(tag)}
              sx={{fontSize: 11, bgcolor: pcolors.blue[50], color: pcolors.blue[700], textTransform: 'capitalize'}}
            />
          ))}
        </Box>
      )}

      {(t?.riskFlags?.length ?? 0) > 0 && (
        <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5}}>
          <Typography
            sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'uppercase', letterSpacing: 0.4}}
          >
            flags
          </Typography>
          {t!.riskFlags.map(flag => (
            <Chip
              key={flag}
              size="small"
              label={humanizeFlag(flag)}
              sx={{fontSize: 11, bgcolor: pcolors.amber[50], color: pcolors.amber[700], textTransform: 'capitalize'}}
            />
          ))}
        </Box>
      )}

      {score.explanation && (
        <Typography sx={{fontSize: 12, color: pcolors.textSecondary, mt: 0.25, lineHeight: 1.5}}>
          {score.explanation}
        </Typography>
      )}
    </Box>
  );
}

/**
 * A single row in the specialists grid.
 *
 * Three rendering modes:
 *   - agent only: the normal case — dot/number colored by `agentScoreTone`.
 *   - agent + feedback: agent row dim + strikethrough, feedback row rendered underneath as the active rating (tooltip explains override).
 *   - feedback only: just the feedback rendering (no agent ever scored this specialization on this video).
 */
function SpecialistRow({entry}: {entry: SpecialistEntry}) {
  const {agent, feedback, specialization} = entry;
  // Selection reason / accent comes from the agent row's gatedContext
  // when present; for feedback-only entries we tag it "Feedback" so the
  // chip stays consistent with the rest of the grid.
  const {text: reasonText, accent} = agent?.gatedContext
    ? selectionLabel(agent.gatedContext.selectionReason, agent.gatedContext.selectionDetail ?? null)
    : agent
      ? {text: 'Legacy score', accent: pcolors.textMuted}
      : {text: 'Feedback', accent: pcolors.amber[500]};

  const active: {score: number; scoreDirection: AgentScore['scoreDirection']; rating: AgentScore['rating']} | undefined =
    feedback || agent;
  const tone = agentScoreTone(active);
  const overriding = !!agent && !!feedback;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        py: 0.5,
        pl: 0.75,
        borderLeft: `3px solid ${accent}`,
        borderBottom: `1px dashed ${pcolors.borderLight}`,
        '&:last-of-type': {borderBottom: 'none'},
      }}
    >
      {/* Primary row — shows the specialization + the active rating's
          score chip + selection-reason pill. */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <Tooltip
          title={
            active
              ? `${feedback ? 'Feedback' : 'Agent'} · Score ${active.score.toFixed(1)}/10 · ${String(active.rating).toLowerCase()}`
              : ''
          }
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: tone.strong,
              flex: 'none',
            }}
          />
        </Tooltip>
        <Typography
          sx={{fontSize: 12, fontWeight: 500, color: pcolors.textPrimary, flex: 1, minWidth: 0}}
          noWrap
        >
          {humanizeSpecialization(specialization)}
        </Typography>
        {feedback && (
          <Tooltip
            title={
              overriding
                ? 'Feedback overrides the agent score for this specialization — the aggregate uses this value instead of the agent rating.'
                : 'User/admin feedback (no agent score exists for this specialization).'
            }
          >
            <Chip
              size="small"
              label={overriding ? 'Feedback override' : 'Feedback only'}
              sx={{
                fontSize: 10,
                height: 18,
                bgcolor: pcolors.amber[50],
                color: pcolors.amber[700],
                fontWeight: 700,
                letterSpacing: 0.3,
              }}
            />
          </Tooltip>
        )}
        <Chip
          size="small"
          label={reasonText}
          sx={{fontSize: 10, height: 18, bgcolor: pcolors.borderLight, color: pcolors.textSecondary}}
        />
      </Box>

      {/* When both exist, show the agent's now-overridden rating on a
          second sub-line so admins can compare the model's call to the
          human's. */}
      {overriding && agent && (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, pl: 1.75, opacity: 0.65}}>
          <Typography
            sx={{
              fontSize: 11,
              color: pcolors.textMuted,
              textDecoration: 'line-through',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            Agent said {agent.score.toFixed(1)} · {String(agent.rating).toLowerCase()}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function SkippedSection({
  skipped,
  triageTags,
  triageFlags,
}: {
  skipped: EvalAgentEntry[];
  triageTags: Set<string>;
  triageFlags: Set<string>;
}) {
  const [open, setOpen] = React.useState(false);
  if (skipped.length === 0) return null;
  return (
    <Box sx={{mt: 1}}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          cursor: 'pointer',
          color: pcolors.textSecondary,
          '&:hover': {color: pcolors.textPrimary},
        }}
      >
        <IconButton size="small" sx={{p: 0.25}}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Typography
          sx={{fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4}}
        >
          {skipped.length} skipped
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 0.75,
            display: 'grid',
            gap: 0.5,
            gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
          }}
        >
          {skipped.map(a => (
            <SkippedRow
              key={`${a.specialization}-${a.version}`}
              agent={a}
              triageTags={triageTags}
              triageFlags={triageFlags}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function SkippedRow({
  agent,
  triageTags: _triageTags,
  triageFlags: _triageFlags,
}: {
  agent: EvalAgentEntry;
  triageTags: Set<string>;
  triageFlags: Set<string>;
}) {
  const reason = reasonForSkip(agent);
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, py: 0.5, opacity: 0.75}}>
      <Box
        sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: pcolors.borderLight, flex: 'none'}}
      />
      <Typography
        sx={{fontSize: 12, color: pcolors.textSecondary, flex: 1, minWidth: 0}}
        noWrap
      >
        {humanizeSpecialization(agent.specialization)}
      </Typography>
      <Typography sx={{fontSize: 10, color: pcolors.textMuted, textTransform: 'lowercase'}} noWrap>
        {reason}
      </Typography>
    </Box>
  );
}

function reasonForSkip(agent: EvalAgentEntry): string {
  const t = agent.triggers;
  if (!t) return 'no triggers defined';
  const tags = t.categoryTags ?? [];
  const flags = t.riskFlags ?? [];
  if (tags.length && flags.length) {
    return `needs category [${tags.map(humanizeTag).join(', ')}] or flag [${flags.map(humanizeFlag).join(', ')}]`;
  }
  if (tags.length) return `needs category [${tags.map(humanizeTag).join(', ')}]`;
  if (flags.length) return `needs flag [${flags.map(humanizeFlag).join(', ')}]`;
  return 'not triggered';
}

function ChannelGateBanner({score}: {score: AgentScore}) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 2,
        bgcolor: pcolors.red[50],
        borderColor: pcolors.red[100],
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <BoltIcon sx={{fontSize: 20, color: pcolors.red[700]}} />
        <Typography sx={{fontSize: 14, fontWeight: 700, color: pcolors.red[700]}}>
          Channel gate — scoring skipped
        </Typography>
      </Box>
      <Typography sx={{fontSize: 12, color: pcolors.textPrimary, mt: 1, lineHeight: 1.55}}>
        {score.explanation}
      </Typography>
      <Typography sx={{fontSize: 11, color: pcolors.textMuted, mt: 1}}>
        No triage, specialists, or finalize agents ran for this video. The pipeline
        skipped all LLM calls because the parent channel was already conclusively
        restricted by prior scoring.
      </Typography>
    </Paper>
  );
}

function FinalRow({score, childAge}: {score: AgentScore; childAge: number}) {
  // Pick the band that contains `childAge`, if the finalize row carries
  // per-age-band verdicts. When matched, override the row's
  // displayed score + rating (and tone) with the band's values so the
  // chain mirrors the VerdictTile's age-aware aggregate. The
  // server-side `pickAgeBandVerdict` does the same thing, but the
  // chain reads from the un-scoped agent_score doc — so we run the
  // equivalent pick on the client.
  const matchedBand = score.ageBandVerdicts?.find(
    v => childAge >= v.minAge && childAge <= v.maxAge,
  );
  const displayScore = matchedBand?.score ?? score.score;
  const displayRating = matchedBand?.rating ?? score.rating;
  const tone = matchedBand
    ? verdictTone(matchedBand.rating)
    : agentScoreTone(score);
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
        <Chip
          size="small"
          label={`Score ${displayScore.toFixed(1)}/10`}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            bgcolor: tone.bg,
            color: tone.fg,
            fontVariantNumeric: 'tabular-nums',
          }}
        />
        <Chip
          size="small"
          label={displayRating.toLowerCase()}
          sx={{
            fontSize: 11,
            textTransform: 'capitalize',
            bgcolor: tone.bg,
            color: tone.fg,
          }}
        />
        {matchedBand && (
          <Chip
            size="small"
            label={`for age ${childAge} (${matchedBand.band.toLowerCase()} ${matchedBand.minAge}–${matchedBand.maxAge})`}
            sx={{
              fontSize: 10,
              fontWeight: 600,
              bgcolor: pcolors.blue[50],
              color: pcolors.blue[700],
            }}
          />
        )}
        {score.model && (
          <Chip
            size="small"
            label={score.model}
            sx={{
              fontSize: 10,
              fontFamily: 'monospace',
              bgcolor: pcolors.borderLight,
              color: pcolors.textSecondary,
            }}
          />
        )}
      </Box>
      {score.explanation && (
        <Typography sx={{fontSize: 12, color: pcolors.textSecondary, lineHeight: 1.55}}>
          {score.explanation}
        </Typography>
      )}
      {score.ageBandVerdicts && score.ageBandVerdicts.length > 0 && (
        <AgeBandVerdictsRow verdicts={score.ageBandVerdicts} childAge={childAge} />
      )}
    </Box>
  );
}

function AgeBandVerdictsRow({
  verdicts,
  childAge,
}: {
  verdicts: AgeBandVerdict[];
  childAge: number;
}) {
  const ordered = [...verdicts].sort(
    (a, b) => AGE_BAND_ORDER.indexOf(a.band) - AGE_BAND_ORDER.indexOf(b.band),
  );
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5}}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: pcolors.textMuted,
        }}
      >
        Per-age verdict
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
        {ordered.map(v => {
          const tone = verdictTone(v.rating);
          // Highlight the band that contains the page's selected age
          // so the breakdown obviously corresponds to what the
          // VerdictTile + FinalRow chips are showing.
          const isSelected = childAge >= v.minAge && childAge <= v.maxAge;
          return (
            <Box
              key={v.band}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                flexWrap: 'wrap',
                borderLeft: isSelected ? `3px solid ${pcolors.blue[500]}` : 'none',
                pl: isSelected ? 0.75 : 0,
              }}
            >
              <Chip
                size="small"
                label={ageBandLabel(v.band, v.minAge, v.maxAge)}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: pcolors.borderLight,
                  color: pcolors.textSecondary,
                  minWidth: 96,
                }}
              />
              <Chip
                size="small"
                label={`${v.score.toFixed(1)} · ${tone.label}`}
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: tone.bg,
                  color: tone.fg,
                  fontVariantNumeric: 'tabular-nums',
                  textTransform: 'capitalize',
                }}
              />
              {v.explanation && (
                <Typography
                  sx={{fontSize: 11, color: pcolors.textSecondary, lineHeight: 1.5, flex: 1, minWidth: 180}}
                >
                  {v.explanation}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
